import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Grid,
  Stack,
  Typography,
  TextField,
  MenuItem,
  Button,
  Autocomplete,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Avatar,
  Divider,
  Alert,
  InputAdornment,
  Skeleton,
  Chip,
  Collapse,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import SaveIcon from "@mui/icons-material/Save";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { api } from "../../utils/api";

const blankLine = () => ({
  service_id: "",
  service_name: "",
  qty: 1,
  price: 0,
  subtotal: 0,
  stages: [], // [{stage_order, stage_name, role_required, user_id}]
  stagesLoading: false,
});

export default function AddJob() {
  const navigate = useNavigate();

  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  // Cache of users per role: { Manager: [...], Team: [...] }
  const [usersByRole, setUsersByRole] = useState({});

  const [form, setForm] = useState({
    client_id: "",
    due_date: "",
    description: "",
  });

  const [lines, setLines] = useState([blankLine()]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  useEffect(() => {
    (async () => {
      try {
        const [cRes, sRes] = await Promise.all([
          api.get("/clients/list.php?page=1&pageSize=1000"),
          api.get("/services/list.php"),
        ]);
        setClients(cRes.clients || []);
        setServices(sRes.services || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const ensureUsersForRole = async (role) => {
    if (!role) return [];
    if (usersByRole[role]) return usersByRole[role];
    try {
      const res = await api.get(
        `/users/by_role.php?role=${encodeURIComponent(role)}`
      );
      const list = res.users || [];
      setUsersByRole((p) => ({ ...p, [role]: list }));
      return list;
    } catch (e) {
      console.error("by_role failed", e);
      setUsersByRole((p) => ({ ...p, [role]: [] }));
      return [];
    }
  };

  const loadStagesForLine = async (index, serviceId) => {
    setLines((old) => {
      const copy = [...old];
      copy[index] = { ...copy[index], stagesLoading: true, stages: [] };
      return copy;
    });
    try {
      const res = await api.get(
        `/workflows/stages_for_service.php?service_id=${serviceId}`
      );
      const stages = (res.stages || []).map((s) => ({
        stage_order: Number(s.stage_order),
        stage_name: s.stage_name,
        role_required: s.role_required,
        user_id: "",
      }));
      // Pre-fetch users for every distinct role in the stages
      const roles = Array.from(new Set(stages.map((s) => s.role_required)));
      await Promise.all(roles.map((r) => ensureUsersForRole(r)));

      setLines((old) => {
        const copy = [...old];
        copy[index] = { ...copy[index], stages, stagesLoading: false };
        return copy;
      });
    } catch (e) {
      console.error(e);
      setLines((old) => {
        const copy = [...old];
        copy[index] = { ...copy[index], stages: [], stagesLoading: false };
        return copy;
      });
    }
  };

  const addLine = () => setLines((l) => [...l, blankLine()]);

  const removeLine = (index) =>
    setLines((l) => l.filter((_, i) => i !== index));

  const updateLine = (index, key, value) => {
    setLines((old) => {
      const updated = [...old];
      updated[index] = { ...updated[index], [key]: value };
      if (key === "qty" || key === "price") {
        updated[index].subtotal =
          (parseFloat(updated[index].qty) || 0) *
          (parseFloat(updated[index].price) || 0);
      }
      return updated;
    });
  };

  const pickService = (index, svc) => {
    setLines((old) => {
      const updated = [...old];
      updated[index] = {
        ...updated[index],
        service_id: svc?.id || "",
        service_name: svc?.name || "",
        price: svc?.default_price || 0,
        qty: updated[index].qty || 1,
        subtotal:
          (parseFloat(updated[index].qty) || 1) *
          (parseFloat(svc?.default_price) || 0),
        stages: [],
      };
      return updated;
    });
    if (svc?.id) loadStagesForLine(index, svc.id);
  };

  const setStageUser = (lineIdx, stageIdx, userId) => {
    setLines((old) => {
      const copy = [...old];
      const line = { ...copy[lineIdx] };
      const stages = line.stages.slice();
      stages[stageIdx] = { ...stages[stageIdx], user_id: userId };
      line.stages = stages;
      copy[lineIdx] = line;
      return copy;
    });
  };

  const grandTotal = lines.reduce(
    (sum, line) => sum + Number(line.subtotal || 0),
    0
  );

  const saveJob = async () => {
    setMsg(null);
    if (!form.client_id || !form.due_date) {
      setMsg({ type: "warning", text: "Client and Due Date are required" });
      return;
    }
    if (!lines.length || lines.some((l) => !l.service_id)) {
      setMsg({
        type: "warning",
        text: "Each line must have a selected service",
      });
      return;
    }
    // First stage of every line must have a user picked
    const missingFirst = lines.find((l) => {
      if (!l.stages?.length) return false;
      const first = l.stages[0];
      return !first.user_id;
    });
    if (missingFirst) {
      setMsg({
        type: "warning",
        text: `Select a user for the first stage of "${missingFirst.service_name}"`,
      });
      return;
    }

    const payload = {
      ...form,
      total: grandTotal,
      lines: lines.map((l) => ({
        service_id: l.service_id,
        service_name: l.service_name,
        qty: l.qty,
        price: l.price,
        subtotal: l.subtotal,
        stage_assignments: (l.stages || []).map((s) => ({
          stage_order: s.stage_order,
          user_id: s.user_id || null,
        })),
      })),
    };

    setSaving(true);
    try {
      await api.post("/jobs/create.php", payload);
      setMsg({ type: "success", text: "Job created successfully" });
      setTimeout(() => navigate("/jobs"), 600);
    } catch (err) {
      setMsg({ type: "error", text: err.message || "Failed to create job" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar sx={{ bgcolor: "primary.main", width: 48, height: 48 }}>
            <WorkOutlineIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Create Job
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Build a new job — pick users for each workflow stage
            </Typography>
          </Box>
        </Stack>
        <Button
          startIcon={<ArrowBackIcon />}
          variant="outlined"
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
      </Stack>

      {msg && (
        <Alert severity={msg.type} sx={{ mb: 2 }}>
          {msg.text}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
          Job Details
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Autocomplete
              options={clients}
              getOptionLabel={(o) => o.business_name || ""}
              value={clients.find((c) => c.id == form.client_id) || null}
              onChange={(_, val) => setField("client_id", val ? val.id : "")}
              renderOption={(props, opt) => (
                <li {...props} key={opt.id}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {opt.business_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      GSTIN: {opt.gstin || "—"} · {opt.mobile || ""}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Client *" fullWidth />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="date"
              label="Due Date *"
              InputLabelProps={{ shrink: true }}
              value={form.due_date}
              onChange={(e) => setField("due_date", e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Description"
              placeholder="Job description…"
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Service Lines
          </Typography>
          <Button size="small" startIcon={<AddCircleIcon />} onClick={addLine}>
            Add Service
          </Button>
        </Stack>

        <Stack spacing={2}>
          {lines.map((line, index) => (
            <Paper
              key={index}
              variant="outlined"
              sx={{ p: 2, bgcolor: "background.default" }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={5}>
                  <Autocomplete
                    options={services}
                    getOptionLabel={(o) =>
                      typeof o === "string" ? o : o.name || ""
                    }
                    value={
                      services.find((s) => s.id === line.service_id) || null
                    }
                    onChange={(_, val) => pickService(index, val)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Service *"
                        placeholder="Pick service…"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={4} md={1}>
                  <TextField
                    size="small"
                    type="number"
                    label="Qty"
                    value={line.qty}
                    onChange={(e) => updateLine(index, "qty", e.target.value)}
                  />
                </Grid>
                <Grid item xs={4} md={2}>
                  <TextField
                    size="small"
                    type="number"
                    label="Price"
                    value={line.price}
                    onChange={(e) => updateLine(index, "price", e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">₹</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={4} md={3}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Subtotal: ₹{Number(line.subtotal || 0).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={1}
                  sx={{ textAlign: { xs: "right", md: "center" } }}
                >
                  {lines.length > 1 && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeLine(index)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </Grid>
              </Grid>

              <Collapse
                in={!!line.service_id}
                timeout="auto"
                unmountOnExit
              >
                <Divider sx={{ my: 2 }} />
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <AccountTreeIcon fontSize="small" color="primary" />
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, textTransform: "uppercase" }}
                  >
                    Workflow Stages — pick user for each
                  </Typography>
                </Stack>

                {line.stagesLoading && <Skeleton height={48} />}

                {!line.stagesLoading && line.stages.length === 0 && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    No stages configured for this service
                  </Alert>
                )}

                {!line.stagesLoading && line.stages.length > 0 && (
                  <Grid container spacing={2}>
                    {line.stages.map((stg, sIdx) => {
                      const roleUsers = usersByRole[stg.role_required] || [];
                      return (
                        <Grid item xs={12} sm={6} md={4} key={sIdx}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              borderLeft: 3,
                              borderLeftColor:
                                sIdx === line.stages.length - 1
                                  ? "success.main"
                                  : "primary.main",
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              sx={{ mb: 1 }}
                            >
                              <Chip
                                label={sIdx + 1}
                                size="small"
                                color="primary"
                              />
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 700 }}
                              >
                                {stg.stage_name}
                              </Typography>
                              <Chip
                                size="small"
                                label={stg.role_required}
                                variant="outlined"
                              />
                            </Stack>
                            <TextField
                              select
                              fullWidth
                              size="small"
                              label={
                                sIdx === 0
                                  ? `Pick ${stg.role_required} *`
                                  : `Pick ${stg.role_required}`
                              }
                              value={stg.user_id || ""}
                              onChange={(e) =>
                                setStageUser(index, sIdx, e.target.value)
                              }
                              helperText={
                                roleUsers.length === 0
                                  ? `No ${stg.role_required} users found`
                                  : ""
                              }
                            >
                              <MenuItem value="">— Unassigned —</MenuItem>
                              {roleUsers.map((u) => (
                                <MenuItem key={u.id} value={u.id}>
                                  {u.name}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </Collapse>
            </Paper>
          ))}
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          spacing={3}
        >
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Total: ₹{grandTotal.toFixed(2)}
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<SaveIcon />}
            onClick={saveJob}
            disabled={saving}
          >
            {saving ? "Saving…" : "Create Job"}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
